import { state } from "../lib/state";
import { apiRequest } from "../lib/api";
import { showToast, formatError } from "../lib/dom";
import { setActiveScreen } from "../lib/router";
import { trackEvent } from "../lib/analytics";
import { useUploadedPerson } from "./studio";
import { refreshBillingState } from "./billing";
import type { CreateProjectRequest, CreateProjectResponse } from "../types/api";

async function submitHairProject(event: Event): Promise<void> {
  event.preventDefault();

  if (!state.user || !state.idToken) {
    showToast("Sign in first.", true);
    setActiveScreen("account");
    return;
  }

  if (!state.selectedHairPerson?.dataUrl) {
    showToast("Upload your photo first.", true);
    return;
  }

  const hairStyleInput = document.getElementById("hair-style") as HTMLInputElement | null;
  const hairColorInput = document.getElementById("hair-color") as HTMLInputElement | null;
  const hairPromptInput = document.getElementById("hair-prompt") as HTMLTextAreaElement | null;

  const hairStyle = hairStyleInput?.value.trim() || "";
  const hairColor = hairColorInput?.value.trim() || "";
  const hairPrompt = hairPromptInput?.value.trim() || "";

  if (!hairStyle && !hairColor && !hairPrompt) {
    showToast("Pick a hair style, a color, or write a prompt.", true);
    return;
  }

  const submitButton = document.getElementById("hair-submit-button") as HTMLButtonElement | null;
  if (submitButton) {
    submitButton.disabled = true;
    submitButton.textContent = "Generating…";
  }

  trackEvent("hair_generation_started", {
    hair: hairStyle || undefined,
    color: hairColor || undefined,
    has_custom_prompt: Boolean(hairPrompt),
  });

  try {
    const body: CreateProjectRequest = {
      inputImage: state.selectedHairPerson.dataUrl,
      hairStyle: hairStyle || undefined,
      hairColor: hairColor || undefined,
      hairPrompt: hairPrompt || undefined,
    };

    await apiRequest<CreateProjectResponse>("/api/create-project", {
      method: "POST",
      body: JSON.stringify(body),
    });

    trackEvent("hair_project_generated", {
      hair: hairStyle || undefined,
      color: hairColor || undefined,
    });
    showToast(
      "Your new hairstyle is being generated. We'll show it in My Closet when it's ready.",
    );
    await refreshBillingState();
    setActiveScreen("closet");
  } catch (error: unknown) {
    const code =
      typeof error === "object" && error !== null && "code" in error
        ? String((error as { code?: unknown }).code || "")
        : undefined;
    trackEvent("hair_project_generation_failure", {
      error_code: code,
      error_message: error instanceof Error ? error.message : String(error),
    });
    showToast(formatError(error), true);
  } finally {
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.textContent = "Try New Hair";
    }
  }
}

export function bind(): void {
  const hairForm = document.getElementById("hair-form") as HTMLFormElement | null;
  const hairPersonInput = document.getElementById("hair-person") as HTMLInputElement | null;
  const hairPersonCamera = document.getElementById("hair-person-camera") as HTMLInputElement | null;
  const hairPromptInput = document.getElementById("hair-prompt") as HTMLTextAreaElement | null;
  const hairPresetButtons = document.querySelectorAll<HTMLElement>(".hair-preset");

  async function handleHairPhotoChange(input: HTMLInputElement): Promise<void> {
    const file = input.files?.[0];
    if (!file) return;
    await useUploadedPerson(file, "hair");
    input.value = "";
  }

  hairPersonInput?.addEventListener("change", () => {
    void handleHairPhotoChange(hairPersonInput);
  });
  hairPersonCamera?.addEventListener("change", () => {
    void handleHairPhotoChange(hairPersonCamera);
  });

  hairPresetButtons.forEach(button => {
    button.addEventListener("click", () => {
      if (hairPromptInput) {
        hairPromptInput.value = button.dataset.prompt || "";
        hairPromptInput.focus();
      }
    });
  });

  hairForm?.addEventListener("submit", event => {
    void submitHairProject(event);
  });
}
